import { useEffect, useState, useCallback } from 'react';
import useCrmStore from '../stores/crmStore';
import LeadDetailPanel from '../components/LeadDetailPanel';
import LostDealModal from '../components/LostDealModal';
import NewLeadModal from '../components/NewLeadModal';
import LeadBadges, { PLAN_META } from '../components/LeadBadges';
import { GripVertical, User, Phone, Mail, Plus, Lock, Crown } from 'lucide-react';

const STAGE_COLORS = {
  nuevo_lead: '#6366f1', contactado: '#2563eb', en_seguimiento: '#d97706',
  promesa_pago: '#7c3aed', cerrado_ganado: '#059669', cerrado_perdido: '#dc2626'
};

// Stages that cannot be dropped into manually. The Cerrado/Ganado column is
// reserved for the App-driven auto-close (webhook E1 cedula match).
const LOCKED_TARGET_STAGES = new Set(['cerrado_ganado']);

function isKompaClubWon(lead) {
  // Source of truth: the App-set `kompa_club_paid` flag + won stage.
  return lead.etapa === 'cerrado_ganado' && Boolean(lead.kompa_club_paid);
}

function LeadCard({ lead, onClick, onDragStart }) {
  const scoreColor = lead.score >= 70 ? '#059669' : lead.score >= 40 ? '#d97706' : '#dc2626';
  const isClub = isKompaClubWon(lead);
  const planMeta = PLAN_META[lead.plan_code];
  return (
    <div
      data-testid={`lead-card-${lead.id}`}
      className={`lead-card${isClub ? ' is-kompa-club' : ''}`}
      draggable
      onDragStart={(e) => { e.dataTransfer.setData('leadId', lead.id); if (onDragStart) onDragStart(lead); }}
      onClick={() => onClick(lead)}
    >
      {isClub && (
        <div data-testid={`badge-kompa-club-${lead.id}`} className="lc-club-badge">
          <Crown size={11} /> KOMPA CLUB
        </div>
      )}
      <div className="lc-top">
        <span className="lc-name" title={`${lead.nombre} ${lead.apellido}`}>
          {lead.nombre} {lead.apellido}
        </span>
        <div className="lc-score" style={{ background: scoreColor }}>{lead.score}</div>
      </div>
      <div className="lc-details">
        <span className="lc-row"><User size={12} /> <span className="lc-val">{lead.cedula || '—'}</span></span>
        <span className="lc-row"><Phone size={12} /> <span className="lc-val">{lead.telefono || '—'}</span></span>
        {lead.email && (
          <span className="lc-row lc-email" title={lead.email}>
            <Mail size={12} /> <span className="lc-val">{lead.email}</span>
          </span>
        )}
        {planMeta && (
          <span className="lc-row lc-plan" style={{ color: planMeta.color }}>
            <planMeta.icon size={12} />
            <span className="lc-val" style={{ fontWeight: 600 }}>{planMeta.label}</span>
          </span>
        )}
      </div>
      <LeadBadges lead={lead} size="sm" />
      <div className="lc-bottom">
        <span className="lc-origin">{lead.origen}</span>
        <span className="lc-agent" title={lead.agente_nombre}>{lead.agente_nombre}</span>
      </div>
      <div className="lc-grip"><GripVertical size={14} /></div>
    </div>
  );
}

function StageColumn({ stage, leads, onCardClick, onDrop }) {
  const [dragOver, setDragOver] = useState(false);
  const stageLeads = leads.filter(l => l.etapa === stage.id);
  const locked = LOCKED_TARGET_STAGES.has(stage.id);

  return (
    <div
      data-testid={`stage-column-${stage.id}`}
      className={`kanban-col${dragOver ? ' drag-over' : ''}${locked ? ' col-locked' : ''}`}
      onDragOver={e => {
        if (locked) return;  // do not allow drop visual on locked columns
        e.preventDefault();
        setDragOver(true);
      }}
      onDragLeave={() => setDragOver(false)}
      onDrop={e => {
        if (locked) return;
        e.preventDefault();
        setDragOver(false);
        const leadId = e.dataTransfer.getData('leadId');
        onDrop(leadId, stage.id);
      }}
    >
      <div className="kol-head" style={{ borderTopColor: stage.color }}>
        <span className="kol-label">
          {locked && <Lock size={11} style={{ marginRight: 4, verticalAlign: 'middle' }} />}
          {stage.label}
        </span>
        <span className="kol-count">{stageLeads.length}</span>
      </div>
      {locked && (
        <div data-testid={`col-locked-hint-${stage.id}`} className="kol-locked-hint">
          Asignacion automatica via App
        </div>
      )}
      <div className="kol-body">
        {stageLeads.map(lead => (
          <LeadCard key={lead.id} lead={lead} onClick={onCardClick} />
        ))}
      </div>
    </div>
  );
}

export default function PipelinePage() {
  const leads = useCrmStore(s => s.leads);
  const stages = useCrmStore(s => s.stages);
  const lossReasons = useCrmStore(s => s.lossReasons);
  const fetchLeads = useCrmStore(s => s.fetchLeads);
  const fetchStages = useCrmStore(s => s.fetchStages);
  const fetchLossReasons = useCrmStore(s => s.fetchLossReasons);
  const moveLead = useCrmStore(s => s.moveLead);

  const [selectedLead, setSelectedLead] = useState(null);
  const [lostDealLead, setLostDealLead] = useState(null);
  const [showNewLead, setShowNewLead] = useState(false);

  useEffect(() => {
    fetchStages();
    fetchLossReasons();
    fetchLeads({ limit: 200 });
  }, [fetchStages, fetchLossReasons, fetchLeads]);

  const handleDrop = useCallback((leadId, targetStage) => {
    const lead = leads.find(l => l.id === leadId);
    if (!lead || lead.etapa === targetStage) return;
    if (LOCKED_TARGET_STAGES.has(targetStage)) return;  // belt-and-braces: never POST a manual close
    if (targetStage === 'cerrado_perdido') {
      setLostDealLead({ id: leadId });
      return;
    }
    moveLead(leadId, targetStage);
  }, [leads, moveLead, setLostDealLead]);

  const handleLostConfirm = async (motivo, notas) => {
    if (lostDealLead) {
      await moveLead(lostDealLead.id, 'cerrado_perdido', motivo, notas);
      setLostDealLead(null);
    }
  };

  const handleRefresh = () => fetchLeads({ limit: 200 });

  return (
    <div data-testid="pipeline-page" className="pipeline-page">
      <div className="pipeline-toolbar">
        <button data-testid="new-lead-button" className="btn-primary" onClick={() => setShowNewLead(true)}>
          <Plus size={16} /> <span>Nuevo Lead</span>
        </button>
      </div>
      <div className="kanban-scroll">
        <div className="kanban-board">
          {stages.map(stage => (
            <StageColumn
              key={stage.id}
              stage={stage}
              leads={leads}
              onCardClick={setSelectedLead}
              onDrop={handleDrop}
            />
          ))}
        </div>
      </div>

      {selectedLead && (
        <LeadDetailPanel
          lead={selectedLead}
          onClose={() => setSelectedLead(null)}
          onUpdate={handleRefresh}
        />
      )}

      {lostDealLead && (
        <LostDealModal
          lossReasons={lossReasons}
          onConfirm={handleLostConfirm}
          onCancel={() => setLostDealLead(null)}
        />
      )}

      {showNewLead && (
        <NewLeadModal
          onClose={() => setShowNewLead(false)}
          onCreated={handleRefresh}
        />
      )}
    </div>
  );
}
